// ─── AONE Design System — Fusion v6 SAP Power features ─────────────────────
// Saved Views, Smart Filter Bar, Object Page panel, Audit Timeline, Comment
// Thread, Inline Edit, Mass Maintenance bulk bar, Drill-Down panel, AI Copilot.
// All wear Fusion C2 dense pastel palette via T.role.<name>.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { createAuthedClient } from '../utils/api';
import { T } from './tokens';

const API = process.env.REACT_APP_API_URL || '';
// One client, built once, that carries the session the way `api` does — a bare
// axios.create() inherits axios's defaults but none of its interceptors, so it
// would send no bearer token and 401 for every user whose oversized aone_sso
// cookie the browser drops.
const fusionClient = createAuthedClient({ baseURL: API, timeout: 20000 });
const ax = () => fusionClient;

const base = { fontFamily: T.font, boxSizing: 'border-box' };
const ACCENT = 'var(--aone-accent, #6E1F2B)';
const ACCENT_HOVER = 'var(--aone-accent-hover, #591722)';
const ACCENT_SOFT = 'var(--aone-accent-soft, #F3E7E6)';
const ACCENT_MID = 'var(--aone-accent-mid, rgba(110, 31, 43,0.18))';
const merge = (...a) => Object.assign({}, ...a.filter(Boolean));

// ─────────────────────────────────────────────────────────────────────────────
// 1. useSavedViews(pageKey) — variant management hook
//    Returns { views, current, setCurrent, save, update, remove, toggleFav }
// ─────────────────────────────────────────────────────────────────────────────
export function useSavedViews(pageKey) {
  const [views, setViews] = useState([]);
  const [currentId, setCurrentId] = useState(() => {
    try { return localStorage.getItem(`aone.savedView.${pageKey}`); } catch { return null; }
  });

  const load = useCallback(async () => {
    try {
      const r = await ax().get('/api/views', { params: { pageKey } });
      setViews(Array.isArray(r.data) ? r.data : []);
    } catch (e) { /* non-fatal — feature degrades to "no saved views" */ }
  }, [pageKey]);

  useEffect(() => { load(); }, [load]);

  const current = views.find(v => v.id === currentId) || null;

  const setCurrent = (id) => {
    setCurrentId(id);
    try { id ? localStorage.setItem(`aone.savedView.${pageKey}`, id) : localStorage.removeItem(`aone.savedView.${pageKey}`); } catch {}
  };

  const save = async (payload) => {
    const r = await ax().post('/api/views', { pageKey, ...payload });
    await load();
    setCurrent(r.data.id);
    return r.data;
  };

  const update = async (id, patch) => {
    const r = await ax().put(`/api/views/${id}`, patch);
    await load();
    return r.data;
  };

  const remove = async (id) => {
    await ax().delete(`/api/views/${id}`);
    if (id === currentId) setCurrent(null);
    await load();
  };

  const toggleFav = async (id) => {
    await ax().post(`/api/views/${id}/favorite`);
    await load();
  };

  return { views, current, currentId, setCurrent, save, update, remove, toggleFav, reload: load };
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. AoneSavedViews — dropdown that surfaces useSavedViews state
// ─────────────────────────────────────────────────────────────────────────────
export function AoneSavedViews({ pageKey, savedViews, onApply, currentFilters, currentColumns, currentSort, onAddFilter }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const { views, current, setCurrent, save, remove, toggleFav } = savedViews;
  const onSaveCurrent = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const v = await save({ name: newName.trim(), filters: currentFilters || {}, columns: currentColumns || null, sort: currentSort || null });
      setNewName('');
      onApply?.(v);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const onPick = (v) => {
    setCurrent(v.id);
    setOpen(false);
    onApply?.(v);
  };

  const onClear = () => {
    setCurrent(null);
    setOpen(false);
    onApply?.(null);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} style={merge(base, {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 10px 6px 12px', borderRadius: 10,
        background: '#FFFFFF', border: `1px solid ${T.border}`,
        fontSize: T.type.sm, fontWeight: 600, color: T.textSec, cursor: 'pointer',
      })}>
        <span style={{ color: T.textTer, fontWeight: 500 }}>View:</span>
        {current?.isFavorite && <span style={{ color: T.role.apricot.ink, fontSize: T.type.sm }}>★</span>}
        <span style={{ color: T.text, fontWeight: 700 }}>{current?.name || 'Default'}</span>
        <span style={{ color: T.textTer, marginLeft: 2 }}>▾</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0,
          background: T.surface, borderRadius: 12, border: `0.5px solid ${T.border}`,
          boxShadow: '0 12px 36px rgba(0,0,0,0.12)', padding: 6, minWidth: 280, zIndex: 50,
        }}>
          <div onClick={onClear} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 10px', borderRadius:8, fontSize:T.type.sm, cursor:'pointer', color: !current ? T.role.mint.ink : T.text, background: !current ? T.role.mint.fill : 'transparent', fontWeight: !current ? 700 : 500 }}>
            <span style={{ width: 14 }}>{!current ? '●' : ''}</span> Default (no filters)
          </div>
          {views.map(v => (
            <div key={v.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 10px', borderRadius:8, fontSize:T.type.sm, cursor:'pointer', color: current?.id === v.id ? T.role.mint.ink : T.text, background: current?.id === v.id ? T.role.mint.fill : 'transparent', fontWeight: current?.id === v.id ? 700 : 500 }}
              onClick={() => onPick(v)}
              onMouseEnter={e => { if (current?.id !== v.id) e.currentTarget.style.background = '#F4F5F7'; }}
              onMouseLeave={e => { if (current?.id !== v.id) e.currentTarget.style.background = 'transparent'; }}
            >
              {/* The hook's mutators reject on failure by design — `save` above
                  catches them. These two were fired from the handler without a
                  catch, so a failed favourite or delete was an unhandled
                  rejection: the star never moved, the view never went, and
                  nothing said why. */}
              <span onClick={(e) => { e.stopPropagation(); toggleFav(v.id).catch(err => alert(err?.response?.data?.error || 'Could not update this view.')); }} style={{ color: v.isFavorite ? T.role.apricot.ink : T.textMuted, fontSize: T.type.sm }}>★</span>
              <span style={{ flex: 1 }}>{v.name}</span>
              {v.isShared && <span style={{ fontSize: T.type.xxs, background: T.role.lilac.fill, color: T.role.lilac.ink, padding: '1px 6px', borderRadius: 100, fontWeight: 700 }}>shared</span>}
              <button onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete view "${v.name}"?`)) remove(v.id).catch(err => alert(err?.response?.data?.error || 'Could not delete this view.')); }}
                aria-label="Delete view"
                style={{ background:'none', border:'none', color: T.textMuted, cursor:'pointer', padding: 4, fontSize: T.type.xs }}>×</button>
            </div>
          ))}
          <div style={{ height: 0.5, background: T.border, margin: '6px 0' }} />
          <div style={{ display: 'flex', gap: 6, padding: 4 }}>
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Save current as…"
              style={{ flex: 1, padding: '7px 10px', border: `1px solid ${T.border}`, borderRadius: 8, fontSize: T.type.sm, fontFamily: T.font, outline: 'none' }}
              onKeyDown={e => { if (e.key === 'Enter') onSaveCurrent(); }}
            />
            <button onClick={onSaveCurrent} disabled={saving || !newName.trim()}
              style={{ padding: '7px 12px', borderRadius: 8, background: ACCENT, color: '#fff', border: 'none', fontSize: T.type.xs, fontWeight: 700, cursor: 'pointer', fontFamily: T.font, opacity: saving || !newName.trim() ? 0.5 : 1 }}>
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. AoneSmartFilterBar — multi-field filter chips with on/off + add
// fields: [{ key, label, options: [{ value, label, role? }] }]
// value: { [fieldKey]: [selectedValues] | string }
// ─────────────────────────────────────────────────────────────────────────────
export function AoneSmartFilterBar({ fields, value, onChange, rightSlot, style }) {
  const [adding, setAdding] = useState(false);
  const v = value || {};

  const removeField = (k) => {
    const next = { ...v };
    delete next[k];
    onChange?.(next);
  };

  const addField = (k) => {
    onChange?.({ ...v, [k]: '' });
    setAdding(false);
  };

  const setFieldValue = (k, val) => {
    onChange?.({ ...v, [k]: val });
  };

  const activeKeys = Object.keys(v).filter(k => v[k] !== undefined);
  const availableFields = fields.filter(f => !activeKeys.includes(f.key));

  return (
    <div style={merge(base, {
      background: T.surface, borderRadius: 12,
      border: `0.5px solid ${T.border}`,
      padding: '10px 12px', marginBottom: 10,
      display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
    }, style)}>
      {activeKeys.map((k, i) => {
        const f = fields.find(x => x.key === k);
        if (!f) return null;
        const role = f.role || 'lilac';
        const r = T.role[role] || T.role.lilac;
        return (
          <React.Fragment key={k}>
            {i > 0 && <span style={{ fontSize: T.type.xxs, fontWeight: 700, letterSpacing: '0.08em', color: T.textTer, padding: '0 4px' }}>AND</span>}
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '6px 4px 6px 12px', borderRadius: 10,
              background: '#F4F5F7', fontSize: T.type.xs, color: T.textSec, fontWeight: 600,
            }}>
              <span style={{ color: T.textTer }}>{f.label}</span>
              <span style={{ color: T.textTer, fontStyle: 'italic' }}>is</span>
              {f.options ? (
                <select value={v[k] || ''} onChange={e => setFieldValue(k, e.target.value)}
                  style={{ border: 'none', background: 'transparent', color: r.ink, fontWeight: 700, fontFamily: T.font, fontSize: T.type.xs, cursor: 'pointer', outline: 'none' }}>
                  <option value="">(any)</option>
                  {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <input value={v[k] || ''} onChange={e => setFieldValue(k, e.target.value)}
                  style={{ border: 'none', background: 'transparent', color: r.ink, fontWeight: 700, fontFamily: T.font, fontSize: T.type.xs, outline: 'none', width: 90 }}
                  placeholder="value"
                />
              )}
              <button onClick={() => removeField(k)} aria-label={`Remove ${f.label} filter`}
                style={{ background: 'none', border: 'none', color: T.textTer, cursor: 'pointer', width: 20, height: 20, borderRadius: 6, fontSize: T.type.base }}>×</button>
            </span>
          </React.Fragment>
        );
      })}

      {availableFields.length > 0 && (
        <div style={{ position: 'relative' }}>
          <button onClick={() => setAdding(!adding)} style={{
            padding: '6px 12px', borderRadius: 10,
            border: `1px dashed ${T.border}`, background: 'transparent',
            color: ACCENT, fontSize: T.type.xs, fontWeight: 700, cursor: 'pointer',
            fontFamily: T.font, display: 'inline-flex', alignItems: 'center', gap: 5,
          }}>+ Add filter</button>
          {adding && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 4px)', left: 0,
              background: T.surface, borderRadius: 10, border: `0.5px solid ${T.border}`,
              boxShadow: '0 8px 28px rgba(0,0,0,0.10)', padding: 4, minWidth: 180, zIndex: 50,
            }}>
              {availableFields.map(f => (
                <div key={f.key} onClick={() => addField(f.key)}
                  style={{ padding: '8px 10px', fontSize: T.type.sm, cursor: 'pointer', borderRadius: 6, color: T.text }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F4F5F7'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  {f.label}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {rightSlot && <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>{rightSlot}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. AoneInlineEdit — click cell to edit, ⏎ to save, ⎋ to cancel
// ─────────────────────────────────────────────────────────────────────────────
export function AoneInlineEdit({ value, onSave, type = 'text', placeholder, style }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value ?? ''); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = async () => {
    if (String(draft) === String(value ?? '')) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave?.(draft);
      setEditing(false);
    } catch (e) {
      console.error('[AoneInlineEdit] save failed', e.message);
      setDraft(value ?? '');
    } finally { setSaving(false); }
  };

  const cancel = () => { setDraft(value ?? ''); setEditing(false); };

  if (!editing) {
    return (
      <span onClick={() => setEditing(true)} style={merge(base, {
        cursor: 'text', display: 'inline-block', padding: '2px 8px',
        borderRadius: 6, position: 'relative',
      }, style)}
        onMouseEnter={e => { e.currentTarget.style.background = ACCENT_SOFT; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
        {value || <span style={{ color: T.textMuted }}>{placeholder || '—'}</span>}
      </span>
    );
  }

  return (
    <span style={merge(base, { display: 'inline-flex', flexDirection: 'column', gap: 3 })}>
      <input ref={inputRef} type={type} value={draft} onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }}
        disabled={saving}
        style={{ padding: '6px 9px', border: `2px solid ${ACCENT}`, borderRadius: 7, fontFamily: T.font, fontSize: T.type.sm, outline: 'none', background: '#fff', minWidth: 80 }}
      />
      <span style={{ fontSize: T.type.xxs, color: T.textTer, fontWeight: 500 }}>{saving ? 'Saving…' : '⏎ Save · ⎋ Cancel'}</span>
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. AoneBulkEditBar — Mass Maintenance floating bar
// ─────────────────────────────────────────────────────────────────────────────
export function AoneBulkEditBar({ count, fields, onApply, onClear, extraActions }) {
  const [fieldKey, setFieldKey] = useState(fields?.[0]?.key || '');
  const [val, setVal] = useState('');
  const [applying, setApplying] = useState(false);

  const f = fields?.find(x => x.key === fieldKey);

  useEffect(() => { setVal(''); }, [fieldKey]);

  if (!count) return null;

  const apply = async () => {
    if (!f || val === '') return;
    setApplying(true);
    try { await onApply?.({ field: fieldKey, value: val }); }
    finally { setApplying(false); }
  };

  return (
    <div role="toolbar" aria-label="Bulk-edit selected rows" style={{
      position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 24,
      background: '#1C1C1E', color: '#fff', padding: '6px 6px 6px 14px',
      borderRadius: 14, display: 'flex', alignItems: 'center', gap: 10,
      fontSize: T.type.sm, fontWeight: 600, fontFamily: T.font, flexWrap: 'wrap',
      boxShadow: '0 8px 32px rgba(0,0,0,0.25), 0 1px 0 rgba(255,255,255,0.08) inset',
      zIndex: 30,
    }}>
      <span style={{ background: T.role.lilac.fill, color: T.role.lilac.ink, padding: '2px 10px', borderRadius: 100, fontWeight: 800, fontSize: T.type.xs }}>
        {count} selected
      </span>
      {fields?.length > 0 && (
        <>
          <span style={{ opacity: 0.65 }}>Set</span>
          <select value={fieldKey} onChange={e => setFieldKey(e.target.value)} style={inkSelect}>
            {fields.map(field => <option key={field.key} value={field.key}>{field.label}</option>)}
          </select>
          <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: T.type.base }}>→</span>
          {f?.options ? (
            <select value={val} onChange={e => setVal(e.target.value)} style={inkSelect}>
              <option value="">(choose)</option>
              {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ) : (
            <input value={val} onChange={e => setVal(e.target.value)} placeholder="value"
              style={{ ...inkSelect, width: 100, backgroundImage: 'none' }} />
          )}
          <button onClick={apply} disabled={applying || val === ''}
            style={{ background: ACCENT, border: 'none', color: '#fff', padding: '6px 12px', borderRadius: 8, fontSize: T.type.xs, fontWeight: 700, cursor: 'pointer', fontFamily: T.font, opacity: applying || val === '' ? 0.5 : 1 }}>
            {applying ? 'Applying…' : `Apply to ${count} row${count !== 1 ? 's' : ''}`}
          </button>
          <span style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.15)' }} />
        </>
      )}
      {extraActions}
      <button onClick={onClear} aria-label="Clear selection" style={{
        background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.6)',
        cursor: 'pointer', padding: 6, fontSize: T.type.md, borderRadius: 8,
        width: 30, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>×</button>
    </div>
  );
}
const inkSelect = {
  background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.15)',
  color: '#fff', padding: '5px 24px 5px 10px', borderRadius: 8,
  fontFamily: T.font, fontSize: T.type.xs, fontWeight: 600, cursor: 'pointer', outline: 'none',
  appearance: 'none',
  backgroundImage: "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23ffffff80' d='M5 6L0 0h10z'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center',
};

// ─────────────────────────────────────────────────────────────────────────────
// 6. AoneDrillPanel — under-KPI drill-down with mini bar charts
// data: [{ title, items: [{ label, value, role? }] }]
// ─────────────────────────────────────────────────────────────────────────────
export function AoneDrillPanel({ panels, style }) {
  if (!panels?.length) return null;
  return (
    <div style={merge(base, {
      marginTop: 10, padding: '14px 16px',
      background: '#FAFAFB', borderRadius: 12,
      border: `0.5px solid ${T.border}`,
      display: 'grid',
      gridTemplateColumns: `repeat(${panels.length}, 1fr)`,
      gap: 18,
    }, style)}>
      {panels.map((p, i) => {
        const max = Math.max(1, ...p.items.map(x => x.value));
        return (
          <div key={i}>
            <div style={{ fontSize: T.type.xs, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.textTer, marginBottom: 8 }}>{p.title}</div>
            {p.items.map((it, j) => {
              const r = T.role[it.role || p.role || 'lilac'];
              return (
                <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, fontSize: T.type.xs }}>
                  <span style={{ flex: '0 0 80px', color: T.textSec, fontWeight: 600, fontSize: T.type.xs, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.label}</span>
                  <span style={{ flex: 1, height: 14, background: T.surface, borderRadius: 4, overflow: 'hidden', border: `0.5px solid ${T.border}` }}>
                    <span style={{ display: 'block', height: '100%', borderRadius: 4, background: r.ink, width: `${(it.value / max) * 100}%` }} />
                  </span>
                  <span style={{ fontSize: T.type.xs, color: T.text, fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 24, textAlign: 'right' }}>{it.value}</span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 7. AoneCopilot — natural-language search input with pastel gradient frame
// ─────────────────────────────────────────────────────────────────────────────
export function AoneCopilot({ placeholder = 'Ask Arey…', value, onChange, onSubmit, style }) {
  return (
    <div style={merge(base, {
      position: 'relative', minWidth: 280, maxWidth: 480, flex: 1,
      background: `linear-gradient(135deg, ${T.role.lilac.fill} 0%, ${T.role.blush.fill} 50%, ${T.role.apricot.fill} 100%)`,
      borderRadius: 12, padding: 2,
      boxShadow: `0 4px 14px ${T.role.lilac.ink}1F`,
    }, style)}>
      {/* minWidth:0 on both the form and the input. A search input keeps an
          intrinsic width of its own and a flex item will not shrink below its
          content, so a caller that passes maxWidth:240 got a box that drew
          itself wider anyway and laid the AREY badge over whatever sat to the
          right of it — on Samples, the Bulk update button. */}
      <form onSubmit={e => { e.preventDefault(); onSubmit?.(value); }} style={{
        background: T.surface, borderRadius: 10, padding: '9px 14px 9px 40px',
        display: 'flex', alignItems: 'center', gap: 8, position: 'relative', minWidth: 0,
      }}>
        <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: T.type.md, color: T.role.lilac.ink, fontWeight: 800 }}>✦</span>
        <input type="search" placeholder={placeholder} value={value || ''} onChange={e => onChange?.(e.target.value)}
          style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', fontFamily: T.font, fontSize: T.type.sm, color: T.text, background: 'transparent' }}
        />
        <span style={{ flex: '0 0 auto', fontSize: T.type.xxs, fontWeight: 700, color: T.role.lilac.ink, background: T.role.lilac.fill, padding: '2px 7px', borderRadius: 100, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>AREY ✦</span>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 8. AoneTimeline — vertical dot timeline (audit + comments interleaved)
// items: [{ kind: 'event'|'comment'|'current', at, who, what, body? }]
// ─────────────────────────────────────────────────────────────────────────────
export function AoneTimeline({ items, style }) {
  if (!items?.length) return <div style={{ color: T.textTer, fontSize: T.type.xs, padding: '14px 0' }}>No activity yet.</div>;
  return (
    <div style={merge(base, { position: 'relative', paddingLeft: 18 }, style)}>
      <div style={{ position: 'absolute', left: 5, top: 6, bottom: 6, width: 1.5, background: T.border }} />
      {items.map((it, i) => {
        const dotColor = it.kind === 'current' ? T.role.coral.ink
                       : it.kind === 'comment' ? T.role.lilac.ink
                       : T.role.mint.ink;
        return (
          <div key={i} style={{ position: 'relative', marginBottom: 14 }}>
            <span style={{
              position: 'absolute', left: -18, top: 3, width: 11, height: 11, borderRadius: '50%',
              border: `2px solid ${T.surface}`, background: dotColor,
              boxShadow: it.kind === 'current' ? `0 0 0 3px ${T.role.coral.fill}` : 'none',
            }} />
            <div style={{ fontSize: T.type.xxs, color: T.textTer, fontWeight: 600 }}>{formatWhen(it.at)}</div>
            <div style={{ fontSize: T.type.sm, color: T.text, fontWeight: 600, marginTop: 2 }}>{it.what || it.body}</div>
            {it.who && <div style={{ fontSize: T.type.xs, color: T.textTer, fontWeight: 500, marginTop: 1 }}>{it.who}</div>}
            {it.kind === 'comment' && it.body && it.what && (
              <div style={{ marginTop: 5, padding: '8px 10px', background: T.role.lilac.fill, borderRadius: 8, fontSize: T.type.xs, color: T.role.lilac.ink, lineHeight: 1.45 }}>{it.body}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}
const formatWhen = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
  } catch { return String(d); }
};

// ─────────────────────────────────────────────────────────────────────────────
// 9. AoneCommentThread — fetches /api/comments/:model/:id, lets user post
// ─────────────────────────────────────────────────────────────────────────────
export function AoneCommentThread({ modelName, modelId, currentUserName = 'You' }) {
  const [comments, setComments] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!modelName || !modelId) return;
    setLoading(true);
    try {
      const r = await ax().get(`/api/comments/${modelName}/${modelId}`);
      setComments(Array.isArray(r.data) ? r.data : []);
    } catch (e) { setComments([]); }
    finally { setLoading(false); }
  }, [modelName, modelId]);

  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!draft.trim()) return;
    setSending(true);
    try {
      // Extract @mentions client-side; backend re-records
      const mentions = (draft.match(/@(\w+)/g) || []).map(m => m.slice(1));
      const r = await ax().post(`/api/comments/${modelName}/${modelId}`, { body: draft.trim(), mentions });
      setComments(c => [...c, r.data]);
      setDraft('');
    } catch (e) { console.error('[AoneCommentThread] send failed', e.message); }
    finally { setSending(false); }
  };

  const renderBody = (text) => {
    const parts = String(text).split(/(@\w+)/g);
    return parts.map((p, i) => p.startsWith('@')
      ? <span key={i} style={{ color: ACCENT, fontWeight: 700 }}>{p}</span>
      : p);
  };

  return (
    <div style={merge(base)}>
      {loading ? <div style={{ color: T.textTer, fontSize: T.type.xs, padding: '10px 0' }}>Loading comments…</div>
       : comments.length === 0
       ? <div style={{ color: T.textTer, fontSize: T.type.xs, padding: '10px 0' }}>No comments yet. Be the first.</div>
       : comments.map(c => {
          const initials = (c.authorName || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
          return (
            <div key={c.id} style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                fontSize: T.type.xs, fontWeight: 700,
                background: `linear-gradient(135deg, ${T.role.lilac.fill}, ${T.role.blush.fill})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: T.role.lilac.ink,
              }}>{initials}</div>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: T.type.xs, fontWeight: 700, color: T.text }}>{c.authorName || 'Unknown'}</span>
                <span style={{ fontSize: T.type.xxs, color: T.textTer, fontWeight: 500, marginLeft: 6 }}>{formatWhen(c.createdAt)}</span>
                <div style={{ fontSize: T.type.sm, color: T.textSec, marginTop: 3, lineHeight: 1.45 }}>{renderBody(c.body)}</div>
              </div>
            </div>
          );
        })}
      <form onSubmit={send} style={{
        display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 10,
        padding: '8px 10px', border: `1px solid ${T.border}`, borderRadius: 10,
        background: '#FAFAFB',
      }}>
        <input value={draft} onChange={e => setDraft(e.target.value)} placeholder="Reply or @mention…"
          style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontFamily: T.font, fontSize: T.type.sm, color: T.text }}
        />
        <button type="submit" disabled={sending || !draft.trim()}
          style={{ padding: '5px 10px', borderRadius: 7, background: ACCENT, color: '#fff', border: 'none', fontSize: T.type.xs, fontWeight: 700, cursor: 'pointer', fontFamily: T.font, opacity: sending || !draft.trim() ? 0.5 : 1 }}>
          {sending ? '…' : 'Send'}
        </button>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 10. AoneObjectPanel — slide-out right side panel with anchored tabs
// Wraps Overview / Timeline / Comments / Attachments / Audit. Caller passes
// modelName + modelId + custom tabs to inject extra panes.
// ─────────────────────────────────────────────────────────────────────────────
export function AoneObjectPanel({
  open, onClose, modelName, modelId,
  title, subtitle, badges,         // header content
  overview,                          // <node> for the Overview tab
  extraTabs,                         // [{ key, label, content }]
  width = 420,
}) {
  const [activeTab, setActiveTab] = useState('overview');
  const [timeline, setTimeline] = useState([]);
  const [tlLoading, setTlLoading] = useState(false);

  useEffect(() => { if (!open) return; setActiveTab('overview'); }, [open, modelId]);

  useEffect(() => {
    if (!open || activeTab !== 'timeline' || !modelName || !modelId) return;
    setTlLoading(true);
    ax().get(`/api/audit/${modelName}/${modelId}`)
      .then(r => setTimeline(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTimeline([]))
      .finally(() => setTlLoading(false));
  }, [open, activeTab, modelName, modelId]);

  if (!open) return null;

  const tabs = [
    { key: 'overview',   label: 'Overview' },
    { key: 'timeline',   label: 'Timeline' },
    { key: 'comments',   label: 'Comments' },
    ...(extraTabs || []),
  ];

  return (
    <aside aria-label={`Details for ${title}`} style={{
      position: 'fixed', top: 0, right: 0, bottom: 0,
      width, maxWidth: '94vw',
      background: T.surface,
      borderLeft: `0.5px solid ${T.border}`,
      boxShadow: '0 12px 36px rgba(0,0,0,0.10)',
      display: 'flex', flexDirection: 'column',
      zIndex: 999,
      fontFamily: T.font,
      animation: 'aoneSlideIn 0.22s cubic-bezier(.16,1,.3,1)',
    }}>
      <style>{`@keyframes aoneSlideIn { from { transform: translateX(20px); opacity: 0; } to { transform: none; opacity: 1; } }`}</style>

      {/* Header */}
      <div style={{ padding: '14px 16px', borderBottom: `0.5px solid ${T.border}` }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1 }}>
            {subtitle && <div style={{ fontFamily: T.fontMono, fontSize: T.type.xs, color: ACCENT, fontWeight: 700, letterSpacing: '-0.01em', background: ACCENT_SOFT, display: 'inline-block', padding: '2px 8px', borderRadius: 6, marginBottom: 6 }}>{subtitle}</div>}
            <div style={{ fontSize: T.type.md, fontWeight: 700, letterSpacing: '-0.01em', color: T.text }}>{title}</div>
          </div>
          <button onClick={onClose} aria-label="Close panel"
            style={{ width: 28, height: 28, borderRadius: 7, background: 'transparent', border: 'none', cursor: 'pointer', color: T.textTer, fontSize: T.type.lg, fontFamily: T.font, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            ×
          </button>
        </div>
        {badges && <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>{badges}</div>}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: `0.5px solid ${T.border}`, padding: '0 8px', gap: 0, overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} role="tab" aria-selected={activeTab === t.key}
            style={{
              padding: '10px 12px', fontSize: T.type.sm, fontWeight: activeTab === t.key ? 700 : 600,
              color: activeTab === t.key ? T.text : T.textTer,
              cursor: 'pointer', border: 'none', background: 'transparent',
              borderBottom: `2px solid ${activeTab === t.key ? ACCENT : 'transparent'}`,
              fontFamily: T.font, whiteSpace: 'nowrap', minHeight: 38,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div data-selectable style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>
        {activeTab === 'overview' && (overview || <div style={{ color: T.textTer, fontSize: T.type.xs }}>No overview details.</div>)}
        {activeTab === 'timeline' && (
          tlLoading ? <div style={{ color: T.textTer, fontSize: T.type.xs }}>Loading…</div>
          : <AoneTimeline items={timeline.map(it => ({
              kind: it.kind === 'comment' ? 'comment' : 'event',
              at: it.at, who: it.who, what: it.kind === 'comment' ? null : (it.action || 'Update'),
              body: it.body || (it.details ? JSON.stringify(it.details) : null),
            }))} />
        )}
        {activeTab === 'comments' && <AoneCommentThread modelName={modelName} modelId={modelId} />}
        {extraTabs?.map(t => activeTab === t.key && <div key={t.key}>{t.content}</div>)}
      </div>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 11. AoneSubscribeBell — toggle subscription on a row
// ─────────────────────────────────────────────────────────────────────────────
export function AoneSubscribeBell({ modelName, modelId, compact = true }) {
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!modelName || !modelId) return;
    ax().get('/api/subscriptions', { params: { modelName, modelId } })
      .then(r => setSubscribed(!!r.data?.subscribed))
      .catch(() => setSubscribed(false));
  }, [modelName, modelId]);

  const toggle = async () => {
    setLoading(true);
    try {
      if (subscribed) {
        await ax().delete(`/api/subscriptions/${modelName}/${modelId}`);
        setSubscribed(false);
      } else {
        await ax().post(`/api/subscriptions/${modelName}/${modelId}`);
        setSubscribed(true);
      }
    } catch {} finally { setLoading(false); }
  };

  const size = compact ? 28 : 32;
  return (
    <button onClick={toggle} disabled={loading} aria-pressed={subscribed} aria-label={subscribed ? 'Unsubscribe' : 'Subscribe to updates'}
      title={subscribed ? 'Subscribed' : 'Subscribe'}
      style={{
        width: size, height: size, borderRadius: 7, border: 'none', cursor: 'pointer',
        background: subscribed ? T.role.apricot.fill : 'transparent',
        color: subscribed ? T.role.apricot.ink : T.textTer,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: T.type.sm, fontFamily: T.font, fontWeight: 700,
      }}>
      🔔
    </button>
  );
}
